import { InfoCircleIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Input, overlayStyle, Button, Stack, Tooltip, Dialog } from '@prophecy/ui-v3';
import { message } from '@prophecy/ui-v3/ui-compat';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { getUserPromptValue, updateUserPromptValue } from '../../../../data/apis/api';
import { useRestMutation, useRestQuery } from '../../../../data/util';

const StyledInputBox = styled(Input)`
  flex: 1;
`;

export const UserPrompt = ({
  value = '',
  errorMsg,
  hasPrompt = false,
  placeholderValue = 'Email Address',
  onChange
}: {
  onChange: (value: string) => void;
  hasPrompt?: boolean;
  value?: string;
  errorMsg?: string;
  placeholderValue?: string;
}) => {
  const [promptUpdated, togglePromptUpdate] = useState(false);

  const { data } = useRestQuery(['UserPromptValue'], getUserPromptValue);
  const userPromptPlaceholder = data?.data?.promptValue || placeholderValue;

  const [promptValue, setPromptValue] = useState(userPromptPlaceholder);

  useEffect(() => {
    setPromptValue(userPromptPlaceholder);
  }, [userPromptPlaceholder]);

  const { mutateAsync, isLoading } = useRestMutation(
    () => {
      return updateUserPromptValue(promptValue);
    },
    {
      sentryTag: 'userPromptUpdateValue'
    }
  );
  const resetPromptValue = () => {
    setPromptValue(placeholderValue);
    togglePromptUpdate(!promptUpdated);
  };

  if (!hasPrompt) {
    return null;
  }

  return (
    <>
      <Dialog
        data-test-id='update-user-prompt-dialog'
        title='Update User Prompt'
        open={promptUpdated}
        onOk={async () => {
          const response = await mutateAsync('');
          onChange(promptValue);
          message.success({ content: response.message });
          resetPromptValue();
        }}
        okButton={{ children: 'Update', loading: isLoading }}
        onClose={resetPromptValue}>
        <Stack padding={`${theme.spaces.x12} 0`}>
          <StyledInputBox value={promptValue} onChange={setPromptValue} data-test-id='user-prompt-dialog-input' />
        </Stack>
      </Dialog>
      <StyledInputBox
        label={
          <Stack gap={theme.spaces.x4} direction='horizontal'>
            Username Prompt
            <Tooltip overlayStyle={overlayStyle} title='The attribute to display in the provided user name prompt.'>
              <div>
                <InfoCircleIcon type='default' color={theme.colors.gray500} />
              </div>
            </Tooltip>
          </Stack>
        }
        onChange={onChange}
        placeholder={userPromptPlaceholder}
        value={value}
        addOnPlacement={'right'}
        addOn={
          <Button variant='plain' onClick={resetPromptValue} data-test-id='user-settings-user-prompt-update-button'>
            Update
          </Button>
        }
        variant={errorMsg ? 'error' : 'primary'}
        helpText={errorMsg}
        data-test-id='user-prompt-input'
      />
    </>
  );
};
